// I² Visual Identity v1 — R1.2 materials module (2026-04-29).
//
// Sibling to ../theme/colors.js. Exposes the Glassmorphism 2.0 toolkit
// as plain style-objects (not CSS strings) so existing inline-style
// components consume via spread:
//
//   import { MATERIALS, RADIUS } from "../theme/materials";
//   <div style={{ ...MATERIALS.thinGlass(), boxShadow: MATERIALS.spatialShadow() }} />
//
// Why functions, not constants: every getter reads getThemeMode() at
// call-time so the same MATERIALS.thinGlass() invocation yields the
// dark or light variant automatically. The mutable `COLORS` object in
// colors.js handles the same pattern for palette tokens; materials
// follow the same shape for consistency.
//
// Two glass tiers (per design doc §5.2):
//   thinGlass   — showcase surfaces (Login, Marketplace browse, Library
//                 landing). 6% opacity dark / 55% opacity light, 20px
//                 blur. Aesthetic carries the screen.
//   thickGlass  — work surfaces (Detail Viewer cards, Builder palette,
//                 Pipeline Jobs container, Admin tabs). 88% opacity
//                 floor + 40px blur on edges only. Data legibility holds.
//
// Three-layer spatial shadow (per design §5.2 + §7.4) replaces every
// existing single-layer box-shadow at adoption time. Theme-aware: dark
// casts denser shadows, light casts soft tinted-navy shadows.
//
// Gradient backdrop is the *backplane* the ambient orbs and glass sit
// against — flat color makes glass look unfinished (per §1 last
// paragraph + §7.x).
//
// Grain is a base64-inlined SVG fractal-noise texture, opacity 3-4% on
// glass overlays. Removes the digital "flat" look at zero asset cost.
//
// All entries are pure data — no React, no DOM. Safe to import in any
// context that imports COLORS today.

import { getThemeMode } from "./colors";

export const RADIUS = {
  outer: 24,   // top-level cards (page panels, dialogs)
  inner: 16,   // form fields, table rows, sub-cards
  deep: 12,    // pills, badges, small tiles
  pill: 999,   // fully-rounded buttons + status indicators
};

// SVG fractal-noise texture, base64-inlined. ~600 chars; smaller than
// any external asset round-trip. Tile is 200×200 — repeats imperceptibly
// across any viewport. Filter renders soft grain across the alpha
// channel; outer rect captures the result.
const GRAIN_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'>` +
  `<filter id='n'>` +
  `<feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>` +
  `<feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0'/>` +
  `</filter>` +
  `<rect width='100%' height='100%' filter='url(#n)'/>` +
  `</svg>`
)}`;

export const MATERIALS = {
  // Showcase-tier glass. 16% white in dark / 72% white in light.
  // Bumped 2026-04-29 moderation pass: 6% / 55% → 16% / 72% because
  // the references show MORE-OPAQUE glass than my first read — text
  // legibility on translucent surfaces failed at lower values. Border
  // opacity bumped too so the card edge reads clearly against orbs.
  thinGlass: () => {
    const mode = getThemeMode();
    return mode === "dark"
      ? {
          background: "rgba(255,255,255,0.10)",
          backdropFilter: "blur(24px) saturate(135%)",
          WebkitBackdropFilter: "blur(24px) saturate(135%)",
          border: "1px solid rgba(255,255,255,0.16)",
          borderRadius: RADIUS.outer,
        }
      : {
          background: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(24px) saturate(135%)",
          WebkitBackdropFilter: "blur(24px) saturate(135%)",
          border: "1px solid rgba(255,255,255,0.55)",
          borderRadius: RADIUS.outer,
        };
  },

  // Work-tier glass. 88% opacity floor + 40px blur on edges only.
  // Data legibility holds; surface still reads as glass at the border.
  thickGlass: () => {
    const mode = getThemeMode();
    return mode === "dark"
      ? {
          background: "rgba(20,24,51,0.88)",
          backdropFilter: "blur(40px) saturate(130%)",
          WebkitBackdropFilter: "blur(40px) saturate(130%)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: RADIUS.outer,
        }
      : {
          background: "rgba(252,250,247,0.88)",
          backdropFilter: "blur(40px) saturate(130%)",
          WebkitBackdropFilter: "blur(40px) saturate(130%)",
          border: "1px solid rgba(14,21,48,0.08)",
          borderRadius: RADIUS.outer,
        };
  },

  // Three-layer spatial shadow. Replaces every standard `box-shadow`
  // at adoption time. Tight + medium + diffused-ambient in one string
  // so consumers can drop it into `boxShadow: ...` directly.
  spatialShadow: () => getThemeMode() === "dark"
    ? "0 1px 2px rgba(5,7,16,0.40), 0 8px 24px rgba(5,7,16,0.24), 0 32px 80px rgba(5,7,16,0.10)"
    : "0 1px 2px rgba(14,21,48,0.10), 0 8px 24px rgba(14,21,48,0.06), 0 32px 80px rgba(14,21,48,0.04)",

  // Gradient backdrop. Applied to root layout containers behind the
  // ambient orbs (the orbs blur picks up the gradient's hue variation
  // and the surface looks alive — flat backgrounds + glass = visibly
  // fake, per §1 of the design doc).
  gradientBackdrop: () => getThemeMode() === "dark"
    ? "linear-gradient(135deg, #0B0E1F 0%, #1B1248 50%, #0B0E1F 100%)"
    : "linear-gradient(135deg, #F5F2EC 0%, #E5EFE9 50%, #EDE5F0 100%)",

  // SVG noise overlay. Apply as a `backgroundImage` on a glass surface's
  // ::after pseudo-element OR as a non-pointer-events absolute child:
  //   <div style={{ position:"absolute", inset:0, opacity:0.03,
  //                 backgroundImage: MATERIALS.grain,
  //                 pointerEvents:"none" }} />
  grain: `url("${GRAIN_SVG}")`,

  // Glass edge-shine — a thin bright gradient on the top edge of a card
  // that mimics light catching on real glass. Apply as a non-pointer
  // absolute child near the top of a glass card:
  //   <div style={{
  //     position:"absolute", top:0, left:0, right:0, height:1,
  //     background: MATERIALS.glassEdgeShine(),
  //     pointerEvents:"none",
  //   }} />
  // Or as a 1-2px linear-gradient overlay across the whole top.
  glassEdgeShine: () => getThemeMode() === "dark"
    ? "linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.06) 40%, transparent 100%)"
    : "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.30) 40%, transparent 100%)",

  // Card-edge glow — a soft saturated halo for "hero" CTAs and the
  // primary card on a hero surface. Returns a string for `boxShadow:`
  // composition with spatialShadow:
  //   boxShadow: `${MATERIALS.spatialShadow()}, ${MATERIALS.cardGlow("accent")}`
  // Color slot is one of accent / cyan / pink / purple — picks from
  // the active palette to keep brand coherence.
  cardGlow: (slot = "accent") => {
    const mode = getThemeMode();
    // Fixed RGB triplets (palette accents are stable across themes).
    const rgb = {
      accent: "79, 70, 229",   // indigo
      cyan:   "6, 182, 212",
      pink:   "236, 72, 153",
      purple: "168, 85, 247",
      coral:  "255, 107, 107",
    }[slot] || "79, 70, 229";
    return mode === "dark"
      ? `0 0 40px rgba(${rgb}, 0.35), 0 0 80px rgba(${rgb}, 0.18)`
      : `0 0 32px rgba(${rgb}, 0.20), 0 0 64px rgba(${rgb}, 0.10)`;
  },

  // Mouse-tracked shiny sheen background. The consumer maintains
  // {x, y} state in pixels relative to the card; this returns the
  // background string for an absolutely-positioned overlay:
  //   <div onMouseMove={trackXY} style={{
  //     position:"absolute", inset:0,
  //     background: MATERIALS.sheen(pos.x, pos.y),
  //     opacity: hovering ? 1 : 0,
  //     transition: "opacity 0.3s",
  //     pointerEvents: "none",
  //     borderRadius: "inherit",
  //   }} />
  sheen: (x, y) => `radial-gradient(circle 240px at ${x}px ${y}px, rgba(255,255,255,0.18), transparent 70%)`,
};

// Ambient-orb palette per theme. Hues sourced from the user's reference
// mockups (visionOS / Linear / 2025-dashboard look):
//   dark  — vibrant violet/coral/cyan/pink, picked up against deep navy
//   light — pastel lavender/peach/mint/apricot, soft on cream backdrop
//
// Used by <AmbientOrbs> (R1.3); also exported here so non-orb surfaces
// can sample matching hues if they need a brand-coherent accent.
export const ORB_HUES = {
  dark:  ["#7C3AED", "#EC4899", "#06B6D4", "#FF6B6B"],
  light: ["#C8B6E2", "#FFB7C5", "#A7E0D6", "#FFD4A3"],
};

// Capability detection. `backdrop-filter` ships everywhere modern but
// Safari < 14 lacks it. Components that care about the fallback can
// branch on this; most consumers don't (the `*Glass` getters return
// solid-with-translucent-rgba which degrades gracefully to a solid
// color on browsers that ignore the filter).
export const SUPPORTS_BACKDROP_FILTER =
  typeof CSS !== "undefined" &&
  CSS.supports &&
  (CSS.supports("backdrop-filter", "blur(1px)") ||
   CSS.supports("-webkit-backdrop-filter", "blur(1px)"));
